import React from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Edit,
  Delete,
  Add,
  FileDownload
} from '@mui/icons-material';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { exportMembersToCSV } from '../../utils/exportCSV/exportMembers';
import ModalMember from './ModalMember';
import ModalResult from '../../components/ModalResult';
import ModalConfirm from '../../components/ModalConfirm';
import { useAdmin } from '../../contexts/AdminContext';

const MemberManagement = () => {
  const { members, users, membershipList, deleteMember } = useAdmin();
  // Funzione per formattare la data come '15-lug-25'
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return dayjs(date).locale('it').format('DD-MMM-YY').toLowerCase();
  };

  const [modal, setModal] = React.useState({ open: false, mode: 'add', member: null });
  const handleOpenModal = (mode, member = null) => setModal({ open: true, mode, member });
  const handleCloseModal = () => setModal({ open: false, mode: 'add', member: null });

  const [resultModal, setResultModal] = React.useState({ open: false, message: '' });
  const [confirmModal, setConfirmModal] = React.useState({ open: false, member: null });

  const handleShowResult = (message) => setResultModal({ open: true, message });
  const handleCloseResult = () => setResultModal({ open: false, message: '' });

  const handleDeleteMember = async () => {
    if (!confirmModal.member) return;
    try {
      const result = await deleteMember(confirmModal.member._id);
      if (result.success) {
        handleShowResult('Tesserato eliminato con successo!');
      } else {
        handleShowResult('Errore durante la cancellazione del tesserato');
      }
    } catch (e) {
      handleShowResult('Errore durante la cancellazione del tesserato');
    } finally {
      handleCloseConfirm();
    }
  };

  const handleOpenConfirm = (member) => setConfirmModal({ open: true, member });
  const handleCloseConfirm = () => setConfirmModal({ open: false, member: null });

  const getMembershipStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'expired':
        return 'warning';
      case 'cancelled':
        return 'error';
      case 'pending':
        return 'info';
      default:
        return 'default';
    }
  };

  const getMembershipStatusLabel = (status) => {
    switch (status) {
      case 'active':
        return 'Attiva';
      case 'expired':
        return 'Scaduta';
      case 'cancelled':
        return 'Cancellata';
      case 'pending':
        return 'In attesa';
      default:
        return status;
    }
  };

  const handleExportCSV = () => {
    exportMembersToCSV(members);
  };

  const memberColumns = [
    { field: 'membershipId', headerName: 'Tessera', flex: 0.7, minWidth: 100, resizable: true },
    {
      field: 'user.fullName',
      headerName: 'Nome',
      flex: 1.5, minWidth: 180, resizable: true,
      renderCell: (params) => {
        const name = params.row.user?.name || '';
        const surname = params.row.user?.surname || '';
        const fullName = `${name} ${surname}`.trim() || 'N/A';
        const email = params.row.user?.email || '';
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body2">{fullName}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {email}
            </Typography>
          </Box>
        );
      },
      valueGetter: (_, user) => {
        const name = user.user?.name || '';
        const surname = user.user?.surname || '';
        const fullName = `${name} ${surname}`.trim() || 'N/A';
        const email = user.user?.email || '';
        return `${fullName} ${email}`.toLowerCase();
      }
    },
    { field: 'amount', headerName: 'Importo', flex: 0.7, minWidth: 90, resizable: true, renderCell: (params) => `€${params.value}` },
    {
      field: 'status',
      headerName: 'Stato',
      flex: 0.7, minWidth: 100, resizable: true,
      renderCell: (params) => (
        <Chip
          label={getMembershipStatusLabel(params.value)}
          color={getMembershipStatusColor(params.value)}
          size="small"
        />
      )
    },
    {
      field: 'startDate',
      headerName: 'Data Inizio',
      flex: 0.8, minWidth: 110, resizable: true,
      renderCell: (params) => formatDate(params.value)
    },
    {
      field: 'endDate',
      headerName: 'Data Fine',
      flex: 0.8, minWidth: 110, resizable: true,
      renderCell: (params) => formatDate(params.value)
    },
    {
      field: 'actions',
      headerName: 'Azioni',
      flex: 1, minWidth: 120, resizable: true,
      renderCell: (params) => (
        <Box>
          {/* <IconButton size="small" color="primary" onClick={() => handleOpenModal('view', params.row)}>
            <Visibility />
          </IconButton> */}
          <IconButton size="small" color="primary" onClick={() => handleOpenModal('edit', params.row)}>
            <Edit />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleOpenConfirm(params.row)}>
            <Delete />
          </IconButton>
        </Box>
      )
    }
  ];

// console.log('members', members);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Gestione Tesserati</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<FileDownload />} onClick={handleExportCSV}>
            Esporta CSV
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenModal('add')}>
            Aggiungi Tesserato
          </Button>
        </Box>
      </Box>
      <DataGrid
        rows={members}
        columns={memberColumns}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25, page: 0 },
          },
        }}
        rowsPerPageOptions={[10, 25, 50]}
        disableSelectionOnClick
        getRowId={(row) => row._id}
        hideFooterPagination={members.length <= 10}
      />
      <ModalMember
        open={modal.open}
        onClose={handleCloseModal}
        mode={modal.mode}
        member={modal.member}
        onSuccess={async (newMember) => {
          // Il Context gestisce automaticamente l'aggiornamento
          handleShowResult(
            modal.mode === 'add' ? 'Tesserato aggiunto con successo!' : 'Tesserato modificato con successo!'
          );
        }}
        users={users}
        membershipList={membershipList}
      />
      <ModalResult open={resultModal.open} onClose={handleCloseResult} message={resultModal.message} />
      <ModalConfirm open={confirmModal.open} onClose={handleCloseConfirm} onConfirm={handleDeleteMember} title="Conferma eliminazione" description={`Sei sicuro di voler eliminare il tesserato con ID "${confirmModal.member?.membershipId}"?`} />
    </Box>
  );
};

export default MemberManagement; 